import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { NavigationItem, NavigationItemInterface } from '../../../navigation/navigation';

interface SearchResult {
  title: string;
  route: string;
  icon: string;
  parentMenu?: string;
  type: 'main' | 'sub';
}

@Component({
  selector: 'app-nav-search',
  templateUrl: './nav-search.component.html',
  styleUrls: ['./nav-search.component.scss']
})
export class NavSearchComponent implements OnInit {
  searchQuery: string = '';
  searchResults: SearchResult[] = [];
  showResults: boolean = false;
  private searchSubject = new Subject<string>();
  private allMenuItems: NavigationItemInterface[] = [];

  constructor(
    private router: Router,
    private navigationItem: NavigationItem
  ) {
    // Debounce search input
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      if (query && query.length >= 2) {
        this.performSearch(query);
      } else {
        this.searchResults = [];
        this.showResults = false;
      }
    });
  }

  ngOnInit(): void {
    // Load all navigation items
    this.allMenuItems = this.navigationItem.get();
  }

  onSearchInput(event: any): void {
    const query = event.target.value.trim();
    this.searchQuery = query;
    this.searchSubject.next(query);
  }

  /**
   * Flatten navigation items and their children into a searchable array
   */
  private flattenMenuItems(items: NavigationItemInterface[], parentTitle?: string): SearchResult[] {
    const results: SearchResult[] = [];
    
    items.forEach(item => {
      // Skip hidden items
      if (item.hidden) {
        return;
      }

      // If item has a URL, add it to results
      if (item.url && item.type === 'item') {
        results.push({
          title: item.title,
          route: item.url,
          icon: item.icon || 'feather icon-circle',
          parentMenu: parentTitle,
          type: parentTitle ? 'sub' : 'main'
        });
      }

      // If item has children, recursively process them
      if (item.children && item.children.length > 0) {
        const childResults = this.flattenMenuItems(item.children, item.title);
        results.push(...childResults);
      }
    });

    return results;
  }

  /**
   * Perform search through menu items
   */
  performSearch(query: string): void {
    this.searchResults = [];
    
    if (!this.allMenuItems || this.allMenuItems.length === 0) {
      this.allMenuItems = this.navigationItem.get();
    }

    // Flatten all menu items
    const flattenedItems = this.flattenMenuItems(this.allMenuItems);
    
    // Filter based on query
    const queryLower = query.toLowerCase();
    const filtered = flattenedItems.filter(item => {
      const titleMatch = item.title.toLowerCase().includes(queryLower);
      const parentMatch = item.parentMenu && item.parentMenu.toLowerCase().includes(queryLower);
      const combinedMatch = `${item.parentMenu || ''} ${item.title}`.toLowerCase().includes(queryLower);
      
      return titleMatch || parentMatch || combinedMatch;
    });

    // Sort results: exact matches first, then by relevance
    const sorted = filtered.sort((a, b) => {
      const aTitleLower = a.title.toLowerCase();
      const bTitleLower = b.title.toLowerCase();
      
      // Exact match gets priority
      if (aTitleLower === queryLower) return -1;
      if (bTitleLower === queryLower) return 1;
      
      // Starts with query gets next priority
      if (aTitleLower.startsWith(queryLower)) return -1;
      if (bTitleLower.startsWith(queryLower)) return 1;
      
      // Then by position of match
      const aIndex = aTitleLower.indexOf(queryLower);
      const bIndex = bTitleLower.indexOf(queryLower);
      if (aIndex !== bIndex) return aIndex - bIndex;
      
      return 0;
    });

    this.searchResults = sorted.slice(0, 10); // Limit to 10 results
    this.showResults = this.searchResults.length > 0;
  }

  onResultClick(result: SearchResult): void {
    if (result.route) {
      this.router.navigate([result.route]);
      this.searchQuery = '';
      this.searchResults = [];
      this.showResults = false;
    }
  }

  onSearchSubmit(event: Event): void {
    event.preventDefault();
    if (this.searchQuery.trim().length >= 2) {
      // If there are results, navigate to first result
      if (this.searchResults.length > 0) {
        this.onResultClick(this.searchResults[0]);
      } else {
        // Otherwise, try to perform search
        this.performSearch(this.searchQuery);
        if (this.searchResults.length > 0) {
          this.onResultClick(this.searchResults[0]);
        }
      }
    }
  }

  onFocus(): void {
    if (this.searchQuery && this.searchQuery.length >= 2) {
      this.showResults = this.searchResults.length > 0;
    }
  }

  onBlur(): void {
    // Delay hiding results to allow click events
    setTimeout(() => {
      this.showResults = false;
    }, 200);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchResults = [];
    this.showResults = false;
  }
}

